import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ─── Types ───────────────────────────────────────────────────────────

export type ObjectiveStatus =
  | "not_started"
  | "in_progress"
  | "at_risk"
  | "on_hold"
  | "complete";

export type ActionItemStatus = "open" | "in_progress" | "done" | "cancelled";
export type ActionItemSource = "exec_meeting" | "one_on_one" | "manual" | "m365";
export type ExtractStatus = "pending" | "ok" | "failed" | "unsupported";

export interface UserOption {
  id: string;
  email: string;
  display_name: string;
}

export interface Quarter {
  id: string;
  year: number;
  quarter_num: number;
  label: string;
  start_date: string;
  end_date: string;
  is_closed: boolean;
}

export interface Attachment {
  id: string;
  objective_id: string;
  filename: string;
  original_filename: string;
  content_type: string;
  byte_size: number;
  url: string;
  extracted_text_status: ExtractStatus;
  created_at: string;
}

export interface Milestone {
  id: string;
  objective_id: string;
  title: string;
  description: string | null;
  weight: number;
  due_date: string | null;
  is_complete: boolean;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
  sort_order: number;
}

export interface Objective {
  id: string;
  quarter_id: string;
  owner_id: string | null;
  owner_name: string | null;
  priority: number;
  title: string;
  definition_of_success: string | null;
  plan_url: string | null;
  plan_body: string | null;
  status: ObjectiveStatus;
  target_date: string | null;
  notes: string | null;
  sort_order: number;
  goal_id: string | null;
  goal_title: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  milestones: Milestone[];
  attachments: Attachment[];
  progress_pct: number;
  weight_total: number;
  weight_warning: boolean;
}

export interface OwnerProgress {
  owner_id: string | null;
  owner_name: string;
  objective_count: number;
  progress_pct: number;
}

export interface QuarterSummary {
  quarter: Quarter;
  objective_count: number;
  completed_count: number;
  progress_pct: number;
  by_owner: OwnerProgress[];
}

export interface ActionItem {
  id: string;
  quarter_id: string | null;
  owner_id: string | null;
  owner_name: string | null;
  title: string;
  description: string | null;
  due_date: string | null;
  status: ActionItemStatus;
  source: ActionItemSource;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface OllamaModelInfo {
  name: string;
  size: number | null;
  is_default: boolean;
}

export interface OllamaModelsResponse {
  models: OllamaModelInfo[];
  configured: boolean;
  default_model: string | null;
}

export interface MilestoneSuggestion {
  title: string;
  description: string | null;
  weight: number;
  due_date: string | null;
}

export interface SuggestMilestonesResponse {
  suggestions: MilestoneSuggestion[];
  model: string;
  raw_text: string | null;
}

export interface WeightSuggestion {
  milestone_id: string;
  weight: number;
  rationale: string | null;
}

export interface SuggestWeightsResponse {
  suggestions: WeightSuggestion[];
  model: string;
  raw_text: string | null;
}

// ─── Query keys ───────────────────────────────────────────────────────

const KEY = ["objectives"] as const;

function qs(params: Record<string, string | null | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

// ─── Users ────────────────────────────────────────────────────────────

export function useUsers() {
  return useQuery({
    queryKey: [...KEY, "users"],
    queryFn: () => apiFetch<UserOption[]>("/objectives/users"),
  });
}

// ─── Quarters ─────────────────────────────────────────────────────────

export function useQuarters() {
  return useQuery({
    queryKey: [...KEY, "quarters"],
    queryFn: () => apiFetch<Quarter[]>("/objectives/quarters"),
  });
}

export function useCreateQuarter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { year: number; quarter_num: number }) =>
      apiFetch<Quarter>("/objectives/quarters", { method: "POST", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateQuarter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Pick<Quarter, "is_closed" | "start_date" | "end_date">> }) =>
      apiFetch<Quarter>(`/objectives/quarters/${id}`, { method: "PATCH", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useQuarterSummary(quarterId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "summary", quarterId],
    queryFn: () => apiFetch<QuarterSummary>(`/objectives/quarters/${quarterId}/summary`),
    enabled: !!quarterId,
  });
}

// ─── Objectives ───────────────────────────────────────────────────────

export function useObjectives(filters: {
  quarterId?: string;
  ownerId?: string;
  status?: string;
}) {
  const { quarterId, ownerId, status } = filters;
  return useQuery({
    queryKey: [...KEY, "list", quarterId, ownerId, status],
    queryFn: () =>
      apiFetch<Objective[]>(
        `/objectives/${qs({ quarter_id: quarterId, owner_id: ownerId, status })}`,
      ),
    enabled: !!quarterId,
  });
}

export interface ObjectiveWritePayload {
  quarter_id?: string;
  title?: string;
  owner_id?: string | null;
  priority?: number;
  definition_of_success?: string | null;
  plan_url?: string | null;
  plan_body?: string | null;
  status?: ObjectiveStatus;
  target_date?: string | null;
  notes?: string | null;
  sort_order?: number;
  goal_id?: string | null;
}

export function useCreateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ObjectiveWritePayload & { quarter_id: string; title: string }) =>
      apiFetch<Objective>("/objectives/", { method: "POST", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ObjectiveWritePayload }) =>
      apiFetch<Objective>(`/objectives/${id}`, { method: "PATCH", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/objectives/${id}`, { method: "DELETE" }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── Goals (for the objective → goal link picker) ─────────────────────

export interface GoalOption {
  id: string;
  title: string;
  short_label: string | null;
}

/** Current-year company goals, for the "link to goal" picker in the objective
 *  form. Cross-year linking (picking a prior year's goal) isn't surfaced yet —
 *  see the goals feature plan. */
export function useGoalOptions() {
  return useQuery({
    queryKey: [...KEY, "goal-options"],
    queryFn: async () => {
      const data = await apiFetch<{ goals: GoalOption[] }>("/goals/");
      return data.goals;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Milestones ───────────────────────────────────────────────────────

export interface MilestoneWritePayload {
  title?: string;
  description?: string | null;
  weight?: number;
  due_date?: string | null;
  is_complete?: boolean;
  notes?: string | null;
  sort_order?: number;
}

export function useCreateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ objectiveId, payload }: { objectiveId: string; payload: MilestoneWritePayload & { title: string } }) =>
      apiFetch<Milestone>(`/objectives/${objectiveId}/milestones`, {
        method: "POST",
        body: payload,
      }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MilestoneWritePayload }) =>
      apiFetch<Milestone>(`/objectives/milestones/${id}`, { method: "PATCH", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useToggleMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      apiFetch<Milestone>(`/objectives/milestones/${id}/complete?value=${value}`, {
        method: "POST",
      }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/objectives/milestones/${id}`, { method: "DELETE" }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useReorderMilestones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ objectiveId, orderedIds }: { objectiveId: string; orderedIds: string[] }) =>
      apiFetch<Milestone[]>(`/objectives/${objectiveId}/milestones/reorder`, {
        method: "POST",
        body: orderedIds.map((id, i) => ({ id, sort_order: i })),
      }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── Attachments ──────────────────────────────────────────────────────

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ objectiveId, file }: { objectiveId: string; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<Attachment>(`/objectives/${objectiveId}/attachments`, {
        method: "POST",
        body: form,
      });
    },
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ objectiveId, attachmentId }: { objectiveId: string; attachmentId: string }) =>
      apiFetch<void>(`/objectives/${objectiveId}/attachments/${attachmentId}`, {
        method: "DELETE",
      }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── AI: milestone suggestions ────────────────────────────────────────

export function useOllamaModels() {
  return useQuery({
    queryKey: [...KEY, "ollama-models"],
    queryFn: () => apiFetch<OllamaModelsResponse>("/objectives/ollama/models"),
    staleTime: 60_000,
  });
}

export function useSuggestMilestones() {
  return useMutation({
    mutationFn: ({
      objectiveId,
      model,
      count_hint,
    }: {
      objectiveId: string;
      model?: string;
      count_hint?: number;
    }) =>
      apiFetch<SuggestMilestonesResponse>(`/objectives/${objectiveId}/suggest-milestones`, {
        method: "POST",
        body: { model, count_hint: count_hint ?? 5, include_attachments: true },
      }),
  });
}

export function useSuggestWeights() {
  return useMutation({
    mutationFn: ({ objectiveId, model }: { objectiveId: string; model?: string }) =>
      apiFetch<SuggestWeightsResponse>(`/objectives/${objectiveId}/suggest-weights`, {
        method: "POST",
        body: { model, include_attachments: true },
      }),
  });
}

// ─── Action items ─────────────────────────────────────────────────────

export interface ActionItemWritePayload {
  title?: string;
  description?: string | null;
  quarter_id?: string | null;
  owner_id?: string | null;
  due_date?: string | null;
  status?: ActionItemStatus;
  source?: ActionItemSource;
}

export function useActionItems(filters: { quarterId?: string; ownerId?: string; status?: string }) {
  const { quarterId, ownerId, status } = filters;
  return useQuery({
    queryKey: [...KEY, "action-items", quarterId, ownerId, status],
    queryFn: () =>
      apiFetch<ActionItem[]>(
        `/objectives/action-items${qs({ quarter_id: quarterId, owner_id: ownerId, status })}`,
      ),
  });
}

export function useCreateActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ActionItemWritePayload & { title: string }) =>
      apiFetch<ActionItem>("/objectives/action-items", { method: "POST", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ActionItemWritePayload }) =>
      apiFetch<ActionItem>(`/objectives/action-items/${id}`, { method: "PATCH", body: payload }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteActionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/objectives/action-items/${id}`, { method: "DELETE" }),
    // Fire-and-forget: don't return the promise, or TanStack Query keeps the
    // mutation in `isPending` until every refetch settles — which makes the
    // dialog/button hang on the full-list refetch instead of the fast POST.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
